import axios from 'axios';
import { AppBridge } from '../../App';
import { isLogEnabled } from '../../Utils/constants';
import { EVENTCODES, pushEventToParent } from '../../Utils/eventUtils';
import { captureException } from '../../Utils/sentryUtils';
import {
  getAccessToken,
  getAltJourney,
  getRefreshToken,
  getSessionId,
  getTenant,
  getUserId,
  setAccessToken,
} from '../../Utils/storageUtils';
import { altJourneyType, TenantType } from '../../Utils/types';
import { showSessionTimeoutModal } from '../../Utils/utils';


// --- Rate Limiter ---
const RATE_LIMIT_WINDOW_MS = 60_000; // 1 minute window
const RATE_LIMIT_MAX_REQUESTS = 50; // max requests per URL per window

interface RateLimitEntry {
  timestamps: number[];
}

const rateLimitMap = new Map<string, RateLimitEntry>();

function normalizeUrl(url: string | undefined): string {
  if (!url) return '';
  // Strip query string, then dynamic path segments (UUIDs, numeric IDs) to group similar endpoints
  return url.split('?')[0].replace(/\/[0-9a-f-]{36}/gi, '/:id').replace(/\/\d+/g, '/:id');
}

function isRateLimited(url: string | undefined): boolean {
  const key = normalizeUrl(url);
  const now = Date.now();

  // Clean up stale entries to prevent unbounded map growth
  rateLimitMap.forEach((val, mapKey) => {
    val.timestamps = val.timestamps.filter(
      (t) => now - t < RATE_LIMIT_WINDOW_MS
    );
    if (val.timestamps.length === 0) {
      rateLimitMap.delete(mapKey);
    }
  });

  let entry = rateLimitMap.get(key);

  if (!entry) {
    entry = { timestamps: [now] };
    rateLimitMap.set(key, entry);
    return false;
  }

  if (entry.timestamps.length >= RATE_LIMIT_MAX_REQUESTS) {
    return true;
  }

  entry.timestamps.push(now);
  return false;
}

function isJwtExpired(token: string | null | undefined): boolean {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

function isRefreshTokenExpired(): boolean {
  return isJwtExpired(getRefreshToken());
}

let refreshInFlight: Promise<string | undefined> | null = null;

// Shared by the request and 401 interceptors so concurrent calls trigger a single refresh
const refreshAuthToken = (): Promise<string | undefined> => {
  if (!refreshInFlight) {
    refreshInFlight = doRefreshAuthToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Function to refresh the token (this should return a Promise with the new token)
const doRefreshAuthToken = async () => {
  try {
    const sessionId = getSessionId();
    const response = await pfmClientAxiosInstance.post(
      `${process.env.REACT_APP_PFM_CLIENT_BASEURL}/auth/refreshToken`,
      {
        refreshToken: getRefreshToken(),
        sessionId: sessionId,
        channelType:
          getTenant() === TenantType.YESBANK
            ? getAltJourney() === altJourneyType.YESONLINE
              ? 'ONLINE'
              : 'IRIS'
            : null,
      }
    );
    if (response) {
      const { accessToken } = response.data;
      setAccessToken(accessToken);
      return accessToken;
    }
  } catch (error: any) {
    captureException(error);
    console.error('Failed to refresh token:', error?.response?.status);
    throw error;
  }
};

function injectUserIdIntoUrl(url: string, userId: string): string {
  const pattern = /(\/(consent|dashboard|auth))(\/[^/]+)/;
  return url.replace(pattern, `$1/${userId}$3`);
}

const pfmClientAxiosInstance = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
});

pfmClientAxiosInstance.interceptors.request.use(
  async (config) => {
    if (isRateLimited(config.url)) {
      const normalizedPath = normalizeUrl(config.url);
      if (isLogEnabled) {
        console.warn(`Rate limit exceeded for: ${normalizedPath}`);
      }
      captureException(new Error(`Rate limit exceeded for: ${normalizedPath}`));
      return Promise.reject(new axios.Cancel('Rate limited'));
    }

    let accessToken = getAccessToken();
    if (accessToken && !config.url?.includes('refreshToken')) {
      // Both tokens expired: the session cannot be recovered, so don't hit the backend
      // just to get a 401. Cancelled requests are swallowed by the response interceptor.
      if (isJwtExpired(accessToken) && isRefreshTokenExpired()) {
        if (isLogEnabled) {
          console.log('Access and refresh tokens expired — skipping request');
        }
        await handleSessionTimeoutOnce();
        return Promise.reject(new axios.Cancel('Session expired'));
      }
      // Proactively refresh an expired access token while the refresh token is still valid.
      // On failure keep the old token; the 401 handler below deals with session timeout.
      if (isJwtExpired(accessToken) && !isRefreshTokenExpired()) {
        try {
          accessToken = (await refreshAuthToken()) ?? accessToken;
        } catch {
          // Intentionally ignored: the request proceeds and the 401 handler handles the failure
        }
      }
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    if (
      config.url &&
      getUserId() &&
      !config.url.includes(`/${getUserId()}/`)
    ) {
      config.url = injectUserIdIntoUrl(config.url, getUserId()!);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const isWafRejection = (data: any): boolean =>
  typeof data === 'string' && data.includes('The requested URL was rejected');

const handleWafRejection = async (data: string) => {
  const supportId = data.match(/support ID is:\s*<?([\w-]+)>?/)?.[1];
  const { message } = await import('antd');
  // message.error(
  //   `The requested URL was rejected. Please consult with your administrator.${
  //     supportId ? ` Support ID: ${supportId}` : ''
  //   }`
  // );
  captureException(new Error(`WAF rejected request. Support ID: ${supportId ?? 'unknown'}`));
};

const handleSessionTimeout = async (errorCode?: string) => {
  if (errorCode === 'HEARTBEAT_SESSION_EXPIRED') {
    pushEventToParent({ eventCode: EVENTCODES.SESSION_TIMEOUT_MDS });
  } else {
    pushEventToParent({ eventCode: 'SESSION_TIMEOUT' });
  }
  const { message } = await import('antd');
  message.error('Session Timeout');
  if (getTenant() === TenantType.JFS) {
    AppBridge.closeWebview();
  }
  if (
    getTenant() === TenantType.YESBANK &&
    getAltJourney() === altJourneyType.YESONLINE
  ) {
    showSessionTimeoutModal();
  }
};

// Pollers can fire many requests while the session is dead; notify the app only once per window
const SESSION_TIMEOUT_DEBOUNCE_MS = 10_000;
let lastSessionTimeoutAt = 0;

const handleSessionTimeoutOnce = async (errorCode?: string) => {
  const now = Date.now();
  if (now - lastSessionTimeoutAt < SESSION_TIMEOUT_DEBOUNCE_MS) return;
  lastSessionTimeoutAt = now;
  await handleSessionTimeout(errorCode);
};

pfmClientAxiosInstance.interceptors.response.use(
  async (response) => {
    if (isWafRejection(response.data)) {
      await handleWafRejection(response.data);
      return Promise.reject(response);
    }
    return response;
  },
  async (error) => {
    // Silently swallow rate-limited requests — don't propagate to components
    if (axios.isCancel(error)) {
      return Promise.resolve(undefined);
    }

    if (isWafRejection(error.response?.data)) {
      await handleWafRejection(error.response.data);
      return Promise.reject(error);
    }

    if (
      error.response &&
      error.response.status === 401 &&
      error.config.url.includes('/pfm-sdk') &&
      !error.config.url.includes('/refreshToken')
    ) {
      if (isRefreshTokenExpired()) {
        if (isLogEnabled) {
          console.log('Refresh token already expired — skipping backend call');
        }
        await handleSessionTimeoutOnce();
        return Promise.resolve(undefined);
      }

      try {
        const newAccessToken = await refreshAuthToken();
        error.config.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return pfmClientAxiosInstance(error.config);
      } catch (refreshError: any) {
        captureException(refreshError);
        if (isLogEnabled) {
          console.log('Refresh Token failed');
        }
        const errorCode =
          refreshError?.response?.data?.errorCode;
        await handleSessionTimeoutOnce(errorCode);
        // return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default pfmClientAxiosInstance;
